import React, { useState, useEffect, useCallback } from 'react';
import { useUrlState } from '../../hooks/useUrlState.ts';
import { useNavigate } from 'react-router-dom';
import { employeeAPI, dailyActivityAPI } from '../../services/api.ts';
import { Layout } from '../../components/Layout.tsx';
import FilterModal from '../../components/FilterModal.tsx';
import dayjs from 'dayjs';
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { Info, Search } from 'lucide-react';
import EmployeeInfoModal from '../../components/Modal/EmployeeInfo.tsx';
import {
  PageHeader,
  StatusBadge,
  TablePagination,
} from '../../components/app/index.ts';
import { isUnsynced, recordRef } from '../../lib/records.ts';

export const DailyActivity = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  // Kept in the address, so the list comes back as it was left.
  const [page, setPage] = useUrlState('page', 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useUrlState('search', '', {
    resetPage: true,
  });
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filteredActivities, setFilteredActivities] = useState<any[]>([]);
  const [filterByGroup, setFilterByGroup] = useUrlState('group', '');
  const [from, setFrom] = useUrlState('from', '', { resetPage: true });
  const [to, setTo] = useUrlState('to', '', { resetPage: true });
  const [filterDataByGroup, setFilterDataByGroup] = useState<any[]>([]);
  const [checkboxStates, setCheckboxStates] = useState(filterByGroup);
  const [dateFilter, setDateFilter] = useState<boolean>(false);
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState<any>(null);

  const checkboxProps = [
    { id: 'employee', label: 'Group of Employee' },
    { id: 'company', label: 'Group of Company' },
  ];

  // The popup's choices go into the address; the list follows the address.
  const handleApply = (
    startDate: string,
    endDate: string,
    checkboxState: any
  ) => {
    if (startDate && endDate) {
      setFrom(startDate);
      setTo(endDate);
    }
    if (checkboxState) setFilterByGroup(checkboxState);
    setIsModalOpen(false);
  };

  const fetchByDates = async (
    startDate: string,
    endDate: string,
    checkboxState: any
  ) => {
    if (checkboxState) {
      setFilterByGroup(checkboxState);
      setIsModalOpen(false);
    }
    try {
      setLoading(true);

      if (startDate && endDate) {
        const response = await dailyActivityAPI.getDailyActivitiesFilter(
          page,
          50,
          startDate,
          endDate
        );
        setActivities(response.data as unknown as any[]);
        setFilteredActivities(response.data as unknown as any[]);
        setDateFilter(true);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalItems(response.pagination?.totalActivities || 0);
        setIsModalOpen(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch daily activities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const fetchDailyActivities = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dailyActivityAPI.getAllDailyActivities(
        page,
        50,
        debouncedSearch
      );
      if (response?.data) {
        setActivities(response.data as unknown as any[]);
        setFilteredActivities(response.data as unknown as any[]);

        setTotalPages(response.pagination?.totalPages || 1);
        setTotalItems(response.pagination?.totalActivities || 0);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch daily activities');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    if (from && to) fetchByDates(from, to, '');
    else fetchDailyActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, fetchDailyActivities, from, to]);

  useEffect(() => {
    if (!filterByGroup) return;
    let groupedActivities: any[] = [];

    // Group by Employee or Company
    if (filterByGroup === 'employee') {
      groupedActivities = activities.reduce((acc: any[], activity) => {
        const employeeName = activity.employee?.name || 'Unknown';

        const existingGroup = acc.find(
          (group) => group.employeeName === employeeName
        );

        if (existingGroup) {
          existingGroup.activities.push(activity);
        } else {
          acc.push({ employeeName, activities: [activity] });
        }

        return acc;
      }, []);
    }

    if (filterByGroup === 'company') {
      groupedActivities = activities.reduce((acc: any[], activity) => {
        const companyName = activity.company?.name || 'Unknown';
        const existingGroup = acc.find(
          (group) => group.companyName === companyName
        );

        if (existingGroup) {
          existingGroup.activities.push(activity);
        } else {
          acc.push({ companyName, activities: [activity] });
        }

        return acc;
      }, []);
    }

    setFilterDataByGroup(groupedActivities);
  }, [filterByGroup, activities]);

  const resetFilter = () => {
    setFilterByGroup('');
    setFilterDataByGroup([]);
    setSearchTerm('');
    setCheckboxStates('');
    setDateFilter(false);
    setFrom('');
    setTo('');
    fetchDailyActivities();
  };

  useEffect(() => {
    if (selectedEmployee) {
      setShowEmployeeDetails(true);
      employeeAPI.details(selectedEmployee).then((res) => {
        if (res.data) {
          setEmployeeDetails(res.data);
        }
      });
      return () => {
        setShowEmployeeDetails(false);
        setEmployeeDetails(null);
      };
    }
  }, [selectedEmployee]);

  const COLUMNS = 10;

  // One daily activity. `showInfo` adds the button that opens the employee's details.
  const renderRow = (activity: any, showInfo: boolean) => (
    <Table.Tr key={activity._id}>
      <Table.Td>{recordRef(activity)}</Table.Td>
      <Table.Td>
        <Group gap={6} wrap="nowrap">
          {activity.employee?.name}
          {showInfo && (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label="Employee details"
              onClick={() => setSelectedEmployee(activity.employee_id)}
            >
              <Info size={16} />
            </ActionIcon>
          )}
        </Group>
      </Table.Td>
      <Table.Td>{activity.area_visited}</Table.Td>
      <Table.Td>{activity.description}</Table.Td>
      <Table.Td>{activity.region?.name || 'N/A'}</Table.Td>
      <Table.Td>
        {activity.territory?.map((t: any) => t.name).join(', ') || 'N/A'}
      </Table.Td>
      <Table.Td>{activity.km_traveled?.toFixed(2)} km</Table.Td>
      <Table.Td>{activity.closing_meter?.toFixed(2)}</Table.Td>
      <Table.Td>{activity.company?.name}</Table.Td>
      <Table.Td>{dayjs(activity.date).format('DD/MM/YYYY')}</Table.Td>
    </Table.Tr>
  );

  return (
    <Layout>
      <PageHeader
        title="Daily Activity"
        actions={
          <>
            <Button
              variant="light"
              title="Cross-Match with Odoo"
              onClick={() => navigate('/daily-activity/cross-match')}
            >
              Cross-Match
            </Button>
            <Button onClick={() => setIsModalOpen(true)}>Filter</Button>
            {(filterDataByGroup.length > 0 || dateFilter) && (
              <Button color="red" variant="light" onClick={resetFilter}>
                Reset
              </Button>
            )}
          </>
        }
      />

      <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
        <Stack gap="md">
          <TextInput
            type="search"
            placeholder="Search Daily Activity"
            leftSection={<Search size={16} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
            onFocus={() => {
              setFilterByGroup('');
              setFilterDataByGroup([]);
            }}
          />

          {error && (
            <Alert
              color="red"
              variant="light"
              withCloseButton
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Group justify="center" h={160}>
              <Loader />
            </Group>
          ) : (
            <>
              <Table.ScrollContainer minWidth={1200}>
                <Table withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>ID</Table.Th>
                      <Table.Th>Employee</Table.Th>
                      <Table.Th>Area Visited</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Region</Table.Th>
                      <Table.Th>Territories</Table.Th>
                      <Table.Th>KM Traveled</Table.Th>
                      <Table.Th>Closing Meter</Table.Th>
                      <Table.Th>Company</Table.Th>
                      <Table.Th>Date</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredActivities.length > 0 &&
                      filterDataByGroup.length === 0 &&
                      filteredActivities.map((activity) => renderRow(activity, true))}

                    {filterDataByGroup.map((group, index) => (
                      <React.Fragment key={index}>
                        <Table.Tr>
                          <Table.Td
                            colSpan={COLUMNS}
                            fw={600}
                            bg="var(--mantine-color-default-hover)"
                          >
                            {group.employeeName || group.companyName}
                          </Table.Td>
                        </Table.Tr>
                        {group.activities.map((activity: any) =>
                          renderRow(activity, false),
                        )}
                      </React.Fragment>
                    ))}

                    {filteredActivities.length === 0 &&
                      filterDataByGroup.length === 0 && (
                        <Table.Tr>
                          <Table.Td colSpan={COLUMNS}>
                            <Text ta="center" c="dimmed" py="md">
                              No Daily Activities found
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      )}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>

              <TablePagination
                page={page}
                totalPages={totalPages}
                onChange={setPage}
                totalItems={totalItems}
                pageSize={50}
              />
            </>
          )}
        </Stack>
      </Paper>

      <FilterModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onApply={handleApply}
        setCheckboxStates={setCheckboxStates}
        checkboxStates={checkboxStates}
        checkboxProps={checkboxProps}
      />

      {showEmployeeDetails && employeeDetails && (
        <EmployeeInfoModal
          employee={employeeDetails}
          onClose={() => setShowEmployeeDetails(false)}
        />
      )}
    </Layout>
  );
};
